import React, { useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PlayerRating, displayRating } from "./rating";
import { insigniaForValue } from "./tiers";
import styles from "./RankInsignia.module.scss";

// The rank insignia — "Ladder K", designed together with Mathias (2026-08-19,
// see the design board artifact). The ladder escalates structurally, not just
// in colour:
//   Bronze / Silver / Gold  — bare chevrons (1-3 = sub-tier); the metal climbs.
//   Platinum                — the hexagon plate debuts, holding speed dashes.
//   Diamond                 — the plate holds three escalating diamond cuts
//                             (I/II/III are genuinely different stones).
//   Master                  — single tier: a closed worm ring holding the star.
//   Wormlord                — single tier: the same ring, crowned.
// Pure inline SVG. Each instance gets its own gradient id (a shared id would
// make every badge take the first one's colours); tier colours ride in CSS vars.

const NUMERAL_COUNT = { I: 1, II: 2, III: 3 } as const;
const P = (x: number, y: number) => `${x.toFixed(1)},${y.toFixed(1)}`;

function starPoints(cx: number, cy: number, ro: number): string {
  const ri = ro * 0.42;
  const pts: string[] = [];
  for (let k = 0; k < 10; k++) {
    const a = ((-90 + k * 36) * Math.PI) / 180;
    const r = k % 2 ? ri : ro;
    pts.push(P(cx + r * Math.cos(a), cy + r * Math.sin(a)));
  }
  return pts.join(" ");
}

// A little 4-point glint.
function glint(x: number, y: number, s: number, key: string): React.ReactNode {
  const pts = [
    P(x, y - s), P(x + s * 0.32, y - s * 0.32), P(x + s, y), P(x + s * 0.32, y + s * 0.32),
    P(x, y + s), P(x - s * 0.32, y + s * 0.32), P(x - s, y), P(x - s * 0.32, y - s * 0.32),
  ].join(" ");
  return <polygon key={key} points={pts} fill="#ffffff" />;
}

// Bare chevrons (Bronze/Silver/Gold; 1-3 = sub-tier), centred on (50, cy).
function chevrons(n: number, grad: string, scale = 1, cy = 53): React.ReactNode[] {
  const w = 38 * scale, rise = 6.5 * scale, thick = 5 * scale, gap = 1.8 * scale, h = rise + thick;
  const total = n * h + (n - 1) * gap;
  const y0 = cy - total / 2, x0 = 50 - w / 2;
  const out: React.ReactNode[] = [];
  for (let i = 0; i < n; i++) {
    const y = y0 + i * (h + gap);
    const pts = [
      P(50, y), P(x0 + w, y + rise), P(x0 + w, y + rise + thick),
      P(50, y + thick), P(x0, y + rise + thick), P(x0, y + rise),
    ].join(" ");
    out.push(<polygon key={`c${i}`} points={pts} fill={`url(#${grad})`} />);
  }
  return out;
}

// The hexagon plate (from Platinum up).
const HEX_PTS = "50,15 85,34 85,72 50,91 15,72 15,34";
function hexPlate(rim: number): React.ReactNode[] {
  return [
    <polygon key="hf" className={styles.hexFill} points={HEX_PTS} />,
    <polygon key="hr" className={styles.hexRim} points={HEX_PTS} style={{ strokeWidth: rim }} />,
  ];
}

// Platinum: thick speed dashes, centred as a group (1-3 = sub-tier).
function dashes(n: number, grad: string): React.ReactNode[] {
  const total = 21 + (n - 1) * 14;
  const x0 = 50 - total / 2;
  const out: React.ReactNode[] = [];
  for (let i = 0; i < n; i++) {
    const x = x0 + i * 14;
    out.push(<polygon key={`d${i}`} points={`${P(x, 63)} ${P(x + 9, 63)} ${P(x + 21, 41)} ${P(x + 12, 41)}`} fill={`url(#${grad})`} />);
  }
  return out;
}

// The brilliant-cut stone (crown + pavilion) around (cx, cy), scaled by k.
function brilliant(k: number, cy: number, grad: string, cx = 50, key = "b"): React.ReactNode[] {
  const y0 = cy - 15 * k, y1 = cy - 6 * k, y2 = cy + 17 * k;
  return [
    <polygon key={`${key}1`} points={`${P(cx - 9 * k, y0)} ${P(cx + 9 * k, y0)} ${P(cx + 15 * k, y1)} ${P(cx - 15 * k, y1)}`} fill={`url(#${grad})`} />,
    <polygon key={`${key}2`} points={`${P(cx - 15 * k, y1)} ${P(cx + 15 * k, y1)} ${P(cx, y2)}`} fill={`url(#${grad})`} />,
    <path
      key={`${key}3`}
      d={`M ${P(cx - 15 * k, y1)} L ${P(cx + 15 * k, y1)} M ${P(cx - 9 * k, y0)} L ${P(cx - 6 * k, y1)} L ${P(cx, y2)} M ${P(cx + 9 * k, y0)} L ${P(cx + 6 * k, y1)} L ${P(cx, y2)}`}
      stroke="rgba(9,13,26,.55)"
      strokeWidth="1.6"
      fill="none"
    />,
  ];
}

// Diamond I / II / III — three genuinely different stones.
function diamondGlyph(sub: number, grad: string): React.ReactNode[] {
  if (sub <= 1) return brilliant(1, 53, grad);
  if (sub === 2) {
    return [
      ...brilliant(1, 53, grad),
      <polygon key="hl" points={`${P(44, 40)} ${P(56, 40)} ${P(54, 44)} ${P(46, 44)}`} fill="rgba(255,255,255,.4)" />,
      glint(63, 40, 3.4, "g1"),
      glint(43, 58, 2.6, "g2"),
    ];
  }
  // III: the showpiece — bigger stone, flanking shards, glints.
  return [
    ...brilliant(1.14, 54, grad),
    <polygon key="hl" points={`${P(43, 39)} ${P(57, 39)} ${P(55, 44)} ${P(45, 44)}`} fill="rgba(255,255,255,.45)" />,
    <polygon key="s1" points={`${P(29, 50)} ${P(33, 55)} ${P(29, 60)} ${P(25, 55)}`} fill={`url(#${grad})`} />,
    <polygon key="s2" points={`${P(71, 50)} ${P(75, 55)} ${P(71, 60)} ${P(67, 55)}`} fill={`url(#${grad})`} />,
    glint(64, 42, 3.6, "g1"),
    glint(40, 62, 2.8, "g2"),
    glint(58, 68, 2.4, "g3"),
  ];
}

// Master / Wormlord: the closed worm ring holding the star (crown on top for
// the apex). Headless by design; the crown carries the apex.
function ringStar(grad: string, crowned: boolean): React.ReactNode[] {
  const out: React.ReactNode[] = [
    <circle key="r" cx="50" cy="52" r="26" fill="none" stroke={`url(#${grad})`} strokeWidth="8" />,
    <polygon key="s" points={starPoints(50, 52, 11)} style={{ fill: "var(--c1)" }} />,
  ];
  if (crowned) {
    const y = 15, w = 26, h = 12, x0 = 50 - w / 2;
    out.push(
      <polygon
        key="cr"
        points={`${P(x0, y)} ${P(x0, y - h * 0.55)} ${P(x0 + w * 0.25, y - h * 0.25)} ${P(50, y - h)} ${P(x0 + w * 0.75, y - h * 0.25)} ${P(x0 + w, y - h * 0.55)} ${P(x0 + w, y)}`}
        style={{ fill: "var(--c1)" }}
      />,
      glint(80, 26, 3.6, "gl"),
    );
  }
  return out;
}

// A tier's own icon WITHOUT its plate, for a badge that brings its own (the start
// page's rank badge): Platinum's speed dashes, Diamond's stone, the star ring of
// Master and Wormlord (crowned). Drawn in a 100 x 100 box like the emblems; fills
// come from the gradient `grad` and the star / crown from the CSS variable --c1.
// null for the chevron tiers (Bronze, Silver, Gold).
export function tierGlyph(tierIndex: number, sub: number, grad: string): React.ReactNode[] | null {
  if (tierIndex === 3) return dashes(sub, grad);
  if (tierIndex === 4) return diamondGlyph(sub, grad);
  if (tierIndex === 5) return ringStar(grad, false);
  if (tierIndex >= 6) return ringStar(grad, true);
  return null;
}

function emblemFor(tierIndex: number, sub: number, grad: string): React.ReactNode[] {
  switch (tierIndex) {
    case 0:
    case 1:
    case 2:
      // Bare chevrons have no plate, so let them fill the viewBox — at badge
      // sizes an unscaled stack reads far too small next to its neighbours.
      return chevrons(sub, grad, 1.5, 50);
    case 3:
      return [...hexPlate(3.8), ...dashes(sub, grad)];
    case 4:
      return [...hexPlate(4.2), ...diamondGlyph(sub, grad)];
    case 5:
      return ringStar(grad, false);
    default:
      return ringStar(grad, true);
  }
}

// Hover tooltip in a body-level portal with fixed positioning: badges sit in
// seat rows / table rows whose siblings form their own stacking contexts, so
// a CSS ::after bubble gets painted under the next row (or clipped by a
// scroll container) no matter its z-index. A portal escapes all of that.
function useTipPortal(tip?: string) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const events = tip
    ? {
        onMouseEnter: () => {
          const r = ref.current?.getBoundingClientRect();
          if (!r) return;
          const x = Math.min(Math.max(r.left + r.width / 2, 130), window.innerWidth - 130);
          setPos({ x, y: r.bottom + 8 });
        },
        onMouseLeave: () => setPos(null),
      }
    : {};
  const bubble =
    tip && pos
      ? createPortal(
          <span className={styles.tipBubble} style={{ top: pos.y, left: pos.x }}>
            {tip}
          </span>,
          document.body,
        )
      : null;
  return { ref, events, bubble };
}

// The unhatched egg: shown where a badge will one day sit (no rating yet), so
// the layout never jumps and the empty state has some personality.
function Egg({ size, className, title }: { size: number; className?: string; title?: string }) {
  const { ref, events, bubble } = useTipPortal(title || "Unranked: play an online match to hatch your rank");
  return (
    <span
      ref={ref}
      {...events}
      className={[styles.badge, styles.egg, className].filter(Boolean).join(" ")}
      style={{ width: size, height: size }}
    >
      <svg className={styles.emblem} viewBox="0 0 100 100" aria-hidden="true">
        <path d="M 50 16 C 67 16 75 37 75 53 C 75 71 64 84 50 84 C 36 84 25 71 25 53 C 25 37 33 16 50 16 Z" />
        <polyline points="36,54 43,49 49,56 56,48 63,54" />
        <circle cx="42" cy="34" r="2.4" />
        <circle cx="59" cy="67" r="2.4" />
      </svg>
      {bubble}
    </span>
  );
}

interface Props {
  // Raw OpenSkill rating; takes precedence over `value` when both are given.
  rating?: PlayerRating | null;
  // Alternative: a precomputed display number (e.g. leaderboard rows).
  value?: number | null;
  // Emblem width/height in px.
  size?: number;
  // none = icon only (label rides in the hover title); beside = icon + "Gold II";
  // stacked = icon over the label.
  text?: "none" | "beside" | "stacked";
  // Append the exact display number to the label / title.
  showNumber?: boolean;
  // With no rating: render the unhatched-egg placeholder (same footprint as a
  // badge) instead of nothing, so surrounding layout stays stable.
  placeholder?: boolean;
  // Hover text for the placeholder egg (callers know WHY there's no rank yet:
  // signed out vs no rated games).
  placeholderTitle?: string;
  className?: string;
}

export default function RankInsignia({ rating, value, size = 24, text = "none", showNumber = false, placeholder = false, placeholderTitle, className }: Props) {
  const gradId = "rg" + useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const v = rating ? displayRating(rating) : value;
  const info = v == null ? null : insigniaForValue(v);
  const title = info ? info.label + (showNumber ? ` · ${v}` : "") : "";
  // Icon-only badges explain themselves on hover (hook must run unconditionally).
  const { ref, events, bubble } = useTipPortal(info && text === "none" ? title : undefined);
  if (v == null || !info) return placeholder ? <Egg size={size} className={className} title={placeholderTitle} /> : null;
  const sub = info.numeral ? NUMERAL_COUNT[info.numeral] : 0;
  const tierClass = styles["tier" + info.tier.id[0].toUpperCase() + info.tier.id.slice(1)];

  const emblem = (
    <span
      ref={text === "none" ? ref : undefined}
      {...(text === "none" ? events : {})}
      className={[styles.badge, tierClass, text === "none" ? className : undefined].filter(Boolean).join(" ")}
      style={{ width: size, height: size }}
    >
      <svg className={styles.emblem} viewBox="0 0 100 100" aria-hidden="true">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0.3" y2="1">
            <stop offset="0" className={styles.g1} />
            <stop offset="0.55" className={styles.g2} />
            <stop offset="1" className={styles.g3} />
          </linearGradient>
        </defs>
        {emblemFor(info.tierIndex, sub, gradId)}
      </svg>
      {bubble}
    </span>
  );

  if (text === "none") return emblem;
  return (
    <span className={[styles.wrap, text === "stacked" ? styles.stacked : undefined, className].filter(Boolean).join(" ")}>
      {emblem}
      <span className={styles.label} style={{ color: info.tier.color }}>
        {info.label}
        {showNumber ? <span className={styles.num}>{v}</span> : null}
      </span>
    </span>
  );
}
